import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@/store';
import { useDigitalStore } from '@/store/digital';
import { DigitalHumanMode, PageMode } from '@/store/types';
import { useSpring, animated } from '@react-spring/web';
import styles from './index.module.scss';
import MarkDown from './components/MarkDown';
import { getMentorList } from '@/services/digitalHuman';
import { BotThree } from './components/BotThree';
import BackgroundAnimate from '@/components/BackgroundAnimate';
import { useChat } from '@ai-sdk/react';
import { extractTTSData, getDefaultChatRequestBody, hasTTSData, processResponseContent } from '@/pages/DigitalHuman/util/util';
import { useButton } from '@/pages/DigitalHuman/hooks/useButton';
import WaveComp from '@/pages/DigitalHuman/components/WaveComp';
import { throttle } from 'lodash';
import ShowMessage from '@/pages/DigitalHuman/components/ShowMessage';
import { ttsPlayerManage } from '@/pages/DigitalHuman/util/tts-manage';
import BottomFAQ from '@/pages/DigitalHuman/components/BottomFAQ';
import { useActivityStatus } from '@/pages/DigitalHuman/hooks/useIdleEmit';

const DigitalHuman = () => {
  const { digitalHumanMode, mode, toggleDigitalHumanMode, routeTimeLineDetailId } = useStore();
  const { setCurrentBot, currentBot, sessionId, isIdle, isPlayIndex, isSpeaking, isSpacePressed } = useDigitalStore((state) => state);

  // 添加滚动容器的ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  const getPositionAndScale = () => {
    // if (digitalHumanMode === DigitalHumanMode.FULLSCREEN) {
    //   return {
    //     scale: 1,
    //     x: '1294px',
    //     y: '245px',
    //   };
    // }
    if (digitalHumanMode === DigitalHumanMode.FULLSCREEN) {
      return {
        scale: 0.6,
        x: '-100px',
        y: '930px',
      };
    }

    // if (routeTimeLineDetailId) {
    //   return {
    //     scale: 0.6,
    //     x: '-100px',
    //     y: '930px',
    //   };
    // }

    switch (mode) {
      case PageMode.SILENT:
        return {
          scale: 0.6,
          x: '2059px',
          y: '685px',
        };
      // case PageMode.ROUTE_CHARACTER_LIST:
      // case PageMode.ROUTE_THEME_LIST:
      //   return {
      //     scale: 0.6,
      //     x: '-100px',
      //     y: '930px',
      //   };
      // case PageMode.HOME:
      default:
        return {
          scale: 0.6,
          x: '-100px',
          y: '930px',
        };
    }
  };

  const onToggleDigitalHumanMode = () => {
    toggleDigitalHumanMode();
  }

  const imageSpring = useSpring({
    ...getPositionAndScale(),
    config: {
      duration: 500,
    }
  });

  const init = async () => {
    // const res = await getInit();
    const res = await getMentorList({ noCache: false, mentorId: '404' });
    if (res?.data?.success) {
      const mentor = res?.data?.data?.mentors?.[0] || {};
      console.log("🚀 ~ init ~ mentor:", mentor)
      setCurrentBot({
        ...mentor,
        records: [],
      });
    }
  }
  useEffect(() => {
    init();
  }, []);



  const {
    messages,
    append,
    status,
    setMessages,
    stop,
  } = useChat({
    id: sessionId,
    // 不要使用默认的 API 路由
    // api: `${getHostConfig().host}/api/qaRobot/new/platform/ask/tts`,
    api: 'https://vlog-zhuangyou.shuwen.com/api/qaRobot/new/platform/ask/tts',
    // 自定义请求构建函数
    body: getDefaultChatRequestBody(),
    onResponse: async (response) => {
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`)
      }
      console.log('onResponse', response)
      ttsPlayerManage.start()
    },
    onFinish: (message) => {
      ttsPlayerManage.isReceived = true
      console.log("🚀 ~ useEffect ~ ttsData:", ttsPlayerManage.ttsData)
      console.info('Chat completed:', message)
    },
    streamProtocol: 'text',
    onError: (error) => {
      console.error('Chat error:', error)
    }
  })
  useEffect(() => {
    window.chat_append = append
    window.chat_setMessages = setMessages
  }, [append, setMessages])

  useEffect(() => {
    if (status === 'ready') {
      setMessages((prevMessages) => {
        // 创建一个新的数组，复制之前的所有消息
        const updatedMessages = [...prevMessages]
        // 找到最后一个消息 (假设总是最后一个是需要更新的助理消息)
        const lastMessageIndex = updatedMessages.length - 1
        if (lastMessageIndex >= 0 && updatedMessages[lastMessageIndex].role === 'assistant') {
          try {
            const rawContent = updatedMessages[lastMessageIndex].content
            // 假设 rawContent 是字符串且包含原始流数据
            if (typeof rawContent === 'string' && rawContent.startsWith('data:')) {
              const cleanedContent = processResponseContent(rawContent)
              // 创建一个更新后的消息对象，只改变 content
              updatedMessages[lastMessageIndex] = {
                ...updatedMessages[lastMessageIndex],
                content: cleanedContent,
              }
            }
          }
          catch (error) {
            console.error('Error cleaning message content:', error)
            // 处理错误，可能选择不更新该消息或将其标记为错误
          }
        }
        return updatedMessages
      })

    }
  }, [status, messages, setMessages])

  const [isTextCompleted, setIsTextCompleted] = useState(false)

  useEffect(() => {
    if (status === 'streaming') {
      setIsTextCompleted(false)
      // 获取最后一条消息内容
      const lastMessage = messages[messages.length - 1]
      if (lastMessage && lastMessage.role === 'assistant') {
        const content = lastMessage.content
        if (typeof content === 'string') {
          // 检查是否包含 text_done 标志
          if (content.includes('"type":"text_done"')) {
            setIsTextCompleted(true)
          }
          // 处理TTS内容，传入消息ID
          if (hasTTSData(content)) {
            const ttsData = extractTTSData(content, lastMessage.id || '')
            ttsPlayerManage.ttsData = ttsData
          }
        }
      }
    }
  }, [status, messages])

  // 优化滚动逻辑
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 检测是否为大屏幕
    const isLargeScreen = window.innerWidth > 2560 || window.innerHeight > 1440;
    
    // 检测是否支持smooth滚动
    const supportsSmooth = 'scrollBehavior' in document.documentElement.style;
    
    // 大屏幕或Windows系统可能需要关闭smooth滚动以提高性能
    const shouldUseSmooth = supportsSmooth && !isLargeScreen;
    
    try {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: shouldUseSmooth ? 'smooth' : 'auto'
      });
    } catch (error) {
      // 降级处理
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // 使用更短的节流延迟
  const throttledScrollToBottom = useCallback(
    throttle(scrollToBottom, 200), // 从2000ms减少到200ms
    [scrollToBottom]
  );

  useEffect(() => {
    // 延迟执行滚动，确保DOM更新完成
    const timer = setTimeout(() => {
      throttledScrollToBottom();
    }, 50); // 从20ms增加到50ms，给DOM更多时间渲染

    return () => clearTimeout(timer);
  }, [messages, throttledScrollToBottom, status, isPlayIndex]);

  const handleClearMessages = () => {
    stop();
    setMessages([]);
    ttsPlayerManage.stop()
  }

  useEffect(() => {
    handleClearMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digitalHumanMode])


  const {
    isListening,
    isRecording,
    isTranscribing,
    isAgentProcessing
  } = useButton({
    handleChat: (text: string) => {
      append({
        role: 'user',
        content: text,
      }, {
        body: {
          ...getDefaultChatRequestBody(),
          query: text,
        },
      })
    },
    handleStop: () => {
      ttsPlayerManage.stop()
      stop()
    }
  })

  useActivityStatus(10000, {
    isListening,
    isRecording,
    isSpeaking,
    isTranscribing,
    isAgentProcessing,
    isLLMProcessing: status !== 'ready'
  }, (isIdle) => {
    if (!isIdle) return
    try {
      handleClearMessages()
    } catch (error) {
      console.error('Error fetching mentor list:', error)
    }
  }); // 5秒超时

  return (
    <>
      {/* <BackgroundAnimate
        visible={digitalHumanMode === DigitalHumanMode.FULLSCREEN}
      >
      </BackgroundAnimate> */}

      <animated.div
        className={styles.humanContainer}
        // onClick={onToggleDigitalHumanMode} // 点击切换状态，应该不需要，后续改为麦克风按住 todo
        style={{
          ...imageSpring,
        }}
      >
        <div className={styles.human}>
          <img
            className={styles.humanImg}
            src="https://s.newscdn.cn/file/2025/06/88bfbe7f-1a0b-4626-8eb8-ddf58f247aee.png" alt="" />
          <BotThree
            pageStatus={
              digitalHumanMode === DigitalHumanMode.FULLSCREEN ? 'Chat' : 'Home'
            }
            currentBot={currentBot}
          />

          {
            isIdle && (
              <img className={styles.cornerIcon} src="https://s.newscdn.cn/file/2025/06/ccd1caec-6718-45c2-8845-91ec9cd4584d.svg" alt="" />
            )
          }
        </div>
        <ShowMessage
          isInScreen={digitalHumanMode === DigitalHumanMode.FULLSCREEN}
          isListening={isListening}
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          isAgentProcessing={isAgentProcessing}
        />
        <animated.div
          className={styles.waveWrapper}
          style={{
            opacity: isRecording ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out'
          }}
        >
          <WaveComp />
        </animated.div>
      </animated.div>
      {messages.length > 0 && (
        <animated.div
          className={styles.answer}
          style={{
            opacity: messages.length > 0 ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <div 
            className={styles.answerContent} 
            ref={scrollContainerRef} // 添加ref
          >
            {
              messages.length === 1 && messages[0].role === 'assistant' ?
                <p className={styles.systemMessage}>
                  {messages[0].content}
                </p> :
                messages.map((message, index) => {
                  const isUser = message.role === 'user';
                  const isLastMessage = index === messages.length - 1;
                  const isNeedCursor = !isUser && isLastMessage && status === 'streaming' && !isTextCompleted
                  return (
                    <div
                      key={index}
                      className={`${styles.messageItem} ${isUser ? styles.userMessage : styles.aiMessage}`}
                      style={{
                        marginLeft: isUser ? '0px' : 'auto',
                        marginRight: isUser ? 'auto' : '0px',
                        width: isUser ? 'auto' : undefined,
                      }}
                    >
                      <MarkDown
                        text={processResponseContent(message.content)}
                        showCursor={isNeedCursor}
                        interrupt={!isLastMessage || isSpacePressed}                        
                      />
                    </div>
                  );
                })
            }

            <div 
              className={styles.scrollHelper} 
              ref={scrollTargetRef} // 添加ref，虽然现在不用getElementById了
            ></div>
          </div>
        </animated.div>
      )}
      {/* {
        (mode!==PageMode.SILENT && mode!==PageMode.ROUTE_CHARACTER_TRACK_SINGLE) && (
          <BottomFAQ />
        )
      } */}


    </>
  );
};

export default memo(DigitalHuman); 
